import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AlgorithmId,
  AlgorithmTune,
  AnimationMode,
  LightPoint,
  WizBulb,
  ZoneId,
} from '../types/ambilight';
import { DEFAULT_TUNE } from '../services/colorEngine';
import { captureEngine, type FrameAnalysisResult } from '../services/captureEngine';
import { wizClient } from '../services/wizClient';

const INITIAL_POINTS: LightPoint[] = [
  {
    id: 'top-left',
    label: 'Superior Izquierda',
    shortLabel: 'TL',
    position: 'tl',
    coords: { x: 0, y: 0 },
    color: { r: 140, g: 80, b: 240 },
    rawColor: { r: 140, g: 80, b: 240 },
    brightness: 60,
    dimming: 70,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: 'bulb-1',
  },
  {
    id: 'top',
    label: 'Superior Centro',
    shortLabel: 'T',
    position: 'tc',
    coords: { x: 0.5, y: 0 },
    color: { r: 80, g: 160, b: 255 },
    rawColor: { r: 80, g: 160, b: 255 },
    brightness: 65,
    dimming: 75,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'top-right',
    label: 'Superior Derecha',
    shortLabel: 'TR',
    position: 'tr',
    coords: { x: 1, y: 0 },
    color: { r: 240, g: 80, b: 180 },
    rawColor: { r: 240, g: 80, b: 180 },
    brightness: 70,
    dimming: 80,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'right',
    label: 'Lado Derecho',
    shortLabel: 'R',
    position: 'cr',
    coords: { x: 1, y: 0.5 },
    color: { r: 255, g: 100, b: 80 },
    rawColor: { r: 255, g: 100, b: 80 },
    brightness: 60,
    dimming: 70,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'bottom-right',
    label: 'Inferior Derecha',
    shortLabel: 'BR',
    position: 'br',
    coords: { x: 1, y: 1 },
    color: { r: 255, g: 180, b: 40 },
    rawColor: { r: 255, g: 180, b: 40 },
    brightness: 55,
    dimming: 65,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'bottom',
    label: 'Inferior Centro',
    shortLabel: 'B',
    position: 'bc',
    coords: { x: 0.5, y: 1 },
    color: { r: 245, g: 140, b: 30 },
    rawColor: { r: 245, g: 140, b: 30 },
    brightness: 50,
    dimming: 60,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'bottom-left',
    label: 'Inferior Izquierda',
    shortLabel: 'BL',
    position: 'bl',
    coords: { x: 0, y: 1 },
    color: { r: 40, g: 220, b: 160 },
    rawColor: { r: 40, g: 220, b: 160 },
    brightness: 55,
    dimming: 65,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'left',
    label: 'Lado Izquierdo',
    shortLabel: 'L',
    position: 'cl',
    coords: { x: 0, y: 0.5 },
    color: { r: 0, g: 210, b: 255 },
    rawColor: { r: 0, g: 210, b: 255 },
    brightness: 65,
    dimming: 75,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
  {
    id: 'all',
    label: 'Toda la Pantalla',
    shortLabel: 'ALL',
    position: 'c',
    coords: { x: 0.5, y: 0.5 },
    color: { r: 180, g: 120, b: 255 },
    rawColor: { r: 180, g: 120, b: 255 },
    brightness: 60,
    dimming: 70,
    whiteLevel: 0,
    borderWidth: 80,
    assignedBulbId: null,
  },
];

const INITIAL_BULBS: WizBulb[] = [
  {
    id: 'bulb-1',
    name: 'Foco WiZ 1',
    ip: '192.168.100.127',
    connected: true,
    isPrimary: true,
    maxBrightness: 100,
    assignedPoints: ['top-left'],
  },
];

export function useAmbilight() {
  const [points, setPoints] = useState<LightPoint[]>(() => {
    try {
      const saved = localStorage.getItem('wiz_points_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const claimedBulbs = new Set<string>();
          return INITIAL_POINTS.map((initial) => {
            const match = parsed.find((p: any) => p.id === initial.id);
            if (match) {
              const bId = match.assignedBulbId;
              let finalBulbId: string | null = null;
              if (bId && !claimedBulbs.has(bId)) {
                claimedBulbs.add(bId);
                finalBulbId = bId;
              }
              return {
                ...initial,
                borderWidth: match.borderWidth ?? initial.borderWidth,
                assignedBulbId: finalBulbId,
              };
            }
            return initial;
          });
        }
      }
    } catch {}
    return INITIAL_POINTS;
  });

  const [bulbs, setBulbs] = useState<WizBulb[]>(() => {
    let loadedBulbs = INITIAL_BULBS;
    try {
      const saved = localStorage.getItem('wiz_bulbs_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loadedBulbs = parsed;
        }
      }
    } catch {}

    // Sincronizar assignedPoints de cada foco con los assignedBulbId de points
    try {
      const savedPoints = localStorage.getItem('wiz_points_config');
      const pointList = savedPoints ? JSON.parse(savedPoints) : INITIAL_POINTS;
      return loadedBulbs.map((bulb) => {
        const assignedZids = pointList
          .filter((pt: any) => pt.assignedBulbId === bulb.id)
          .map((pt: any) => pt.id as ZoneId);
        return {
          ...bulb,
          assignedPoints: assignedZids,
        };
      });
    } catch {
      return loadedBulbs;
    }
  });

  const [algorithm, setAlgorithm] = useState<AlgorithmId>(() => {
    try {
      const saved = localStorage.getItem('wiz_algorithm_config');
      if (saved) return saved as AlgorithmId;
    } catch {}
    return 'perceptual';
  });

  const [tune, setTune] = useState<AlgorithmTune>(() => {
    try {
      const saved = localStorage.getItem('wiz_tune_config');
      if (saved) return { ...DEFAULT_TUNE, ...JSON.parse(saved) };
    } catch {}
    return DEFAULT_TUNE;
  });

  const [globalBorderWidth, setGlobalBorderWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('wiz_border_config');
      if (saved) return Number(saved);
    } catch {}
    return 80;
  });

  const [isCapturing, setIsCapturing] = useState(false);
  const [fps, setFps] = useState(60);
  const [animationMode, setAnimationMode] = useState<AnimationMode>(() => {
    try {
      const saved = localStorage.getItem('wiz_mode_config');
      if (saved) return saved as AnimationMode;
    } catch {}
    return 'optimal';
  });
  const [inspectorZone, setInspectorZone] = useState<ZoneId>('top-left');

  const [masterColor, setMasterColor] = useState({ r: 0, g: 0, b: 0 });
  const [masterDimming, setMasterDimming] = useState(50);
  const [masterBrightness, setMasterBrightness] = useState(50);
  const [masterWhite, setMasterWhite] = useState(0);

  // Referencias mutables para que el bucle de 60fps no recree listeners
  const pointsRef = useRef(points);
  pointsRef.current = points;

  const algorithmRef = useRef(algorithm);
  algorithmRef.current = algorithm;

  const tuneRef = useRef(tune);
  tuneRef.current = tune;

  const bulbsRef = useRef(bulbs);
  bulbsRef.current = bulbs;

  const animationModeRef = useRef(animationMode);
  animationModeRef.current = animationMode;

  // Guardar configuración en localStorage de forma inteligente (sin churn de 60fps)
  const lastSavedPointsConfigRef = useRef('');
  useEffect(() => {
    try {
      const pointsConfig = points.map((p) => ({
        id: p.id,
        borderWidth: p.borderWidth,
        assignedBulbId: p.assignedBulbId,
      }));
      const serialized = JSON.stringify(pointsConfig);
      if (serialized !== lastSavedPointsConfigRef.current) {
        lastSavedPointsConfigRef.current = serialized;
        localStorage.setItem('wiz_points_config', serialized);
      }
    } catch {}
  }, [points]);

  useEffect(() => {
    try {
      localStorage.setItem('wiz_bulbs_config', JSON.stringify(bulbs));
    } catch {}
  }, [bulbs]);

  useEffect(() => {
    try {
      localStorage.setItem('wiz_algorithm_config', algorithm);
    } catch {}
  }, [algorithm]);

  useEffect(() => {
    try {
      localStorage.setItem('wiz_tune_config', JSON.stringify(tune));
    } catch {}
  }, [tune]);

  useEffect(() => {
    try {
      localStorage.setItem('wiz_border_config', String(globalBorderWidth));
    } catch {}
  }, [globalBorderWidth]);

  useEffect(() => {
    try {
      localStorage.setItem('wiz_mode_config', animationMode);
    } catch {}
  }, [animationMode]);

  // Manejador de frames procesados por el engine
  const handleFrame = useCallback((res: FrameAnalysisResult) => {
    setFps(res.fps);
    setMasterColor(res.master.adjustedColor);
    setMasterDimming(res.master.dimming);
    setMasterBrightness(res.master.brightness);
    setMasterWhite(res.master.whiteLevel);

    // Actualizar colores calculados en cada punto
    setPoints((prevPoints) =>
      prevPoints.map((pt) => {
        const calculated = res.points[pt.id];
        if (!calculated) return pt;
        return {
          ...pt,
          color: calculated.adjustedColor,
          rawColor: calculated.rawColor,
          brightness: calculated.brightness,
          dimming: calculated.dimming,
          whiteLevel: calculated.whiteLevel,
        };
      })
    );

    // Transmitir a cada foco según su punto físico asignado
    bulbsRef.current.forEach((bulb) => {
      const assignedZid = bulb.assignedPoints && bulb.assignedPoints.length > 0 ? bulb.assignedPoints[0] : null;
      const pointCalc = assignedZid ? res.points[assignedZid] : null;

      const targetColor = pointCalc ? pointCalc.adjustedColor : res.master.adjustedColor;
      const targetDimming = pointCalc ? pointCalc.dimming : res.master.dimming;
      const targetWhite = pointCalc ? pointCalc.whiteLevel : res.master.whiteLevel;
      const targetBright = pointCalc ? pointCalc.brightness : res.master.brightness;

      wizClient.sendColorFrame(
        bulb,
        targetColor,
        targetDimming,
        targetWhite,
        targetBright
      );
    });
  }, []);

  // Iniciar el bucle de procesamiento continuo en el frontend
  useEffect(() => {
    const unsub = captureEngine.subscribe(handleFrame);

    captureEngine.startLoop(
      () => pointsRef.current,
      () => algorithmRef.current,
      () => tuneRef.current,
      () => {
        const prim = bulbsRef.current.find((b) => b.isPrimary);
        return prim ? prim.maxBrightness : 100;
      },
      () => animationModeRef.current
    );

    return () => {
      unsub();
      captureEngine.stopLoop();
    };
  }, [handleFrame]);

  // Métodos de control
  const toggleShareScreen = async () => {
    if (isCapturing) {
      captureEngine.stopCapture();
      setIsCapturing(false);
    } else {
      const ok = await captureEngine.startCapture();
      if (ok) {
        setIsCapturing(true);
      }
    }
  };

  const updatePointBorderWidth = (pointId: string, width: number) => {
    setPoints((prev) =>
      prev.map((pt) => (pt.id === pointId ? { ...pt, borderWidth: width } : pt))
    );
  };

  const applyBorderToAll = (width: number) => {
    setGlobalBorderWidth(width);
    setPoints((prev) => prev.map((pt) => ({ ...pt, borderWidth: width })));
  };

  const assignBulbToPoint = (pointId: string, bulbId: string | null) => {
    setPoints((prev) =>
      prev.map((pt) => {
        // Asignar al punto seleccionado
        if (pt.id === pointId) {
          return { ...pt, assignedBulbId: bulbId };
        }
        // Si el foco que estamos asignando ya estaba en otro punto, lo movemos aquí
        // liberando el punto anterior (un foco físico solo puede estar en 1 lugar real)
        if (bulbId && pt.assignedBulbId === bulbId) {
          return { ...pt, assignedBulbId: null };
        }
        return pt;
      })
    );

    setBulbs((prev) =>
      prev.map((b) => {
        if (b.id === bulbId) {
          // El foco solo tiene 1 punto asignado en el espacio físico
          return { ...b, assignedPoints: [pointId as ZoneId] };
        }
        // Si otro foco estaba en este punto, queda sin punto asignado
        const filtered = (b.assignedPoints || []).filter((p) => p !== pointId);
        return { ...b, assignedPoints: filtered };
      })
    );
  };

  const updateTuneParam = (key: keyof AlgorithmTune, val: number | boolean) => {
    setTune((prev) => ({ ...prev, [key]: val }));
  };

  const addBulb = async (name: string, ip: string): Promise<boolean> => {
    const reachable = await wizClient.connectBulb(ip);
    const newBulb: WizBulb = {
      id: `bulb-${Date.now()}`,
      name,
      ip,
      connected: reachable.reachable ?? false,
      isPrimary: bulbs.length === 0,
      maxBrightness: 100,
      assignedPoints: [],
    };
    setBulbs((prev) => [...prev, newBulb]);
    return !!reachable.reachable;
  };

  const removeBulb = (bulbId: string) => {
    setBulbs((prev) => {
      const filtered = prev.filter((b) => b.id !== bulbId);
      if (filtered.length > 0 && !filtered.some((b) => b.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
    // Limpiar asignaciones en puntos
    setPoints((prev) =>
      prev.map((pt) => (pt.assignedBulbId === bulbId ? { ...pt, assignedBulbId: null } : pt))
    );
  };

  const setPrimaryBulb = (bulbId: string) => {
    setBulbs((prev) =>
      prev.map((b) => ({
        ...b,
        isPrimary: b.id === bulbId,
      }))
    );
    const targetBulb = bulbs.find((b) => b.id === bulbId);
    if (targetBulb) {
      wizClient.connectBulb(targetBulb.ip);
    }
  };

  // Alterna o asigna la ubicación física única del foco a un punto
  const togglePointAssignment = (bulbId: string, pointId: ZoneId) => {
    setPoints((prev) => {
      const targetPt = prev.find((p) => p.id === pointId);
      const isCurrentlyHere = targetPt?.assignedBulbId === bulbId;
      const nextBulbId = isCurrentlyHere ? null : bulbId;

      return prev.map((pt) => {
        if (pt.id === pointId) {
          return { ...pt, assignedBulbId: nextBulbId };
        }
        // Si el foco se asigna a pointId, se libera de su ubicación previa
        if (nextBulbId && pt.assignedBulbId === nextBulbId) {
          return { ...pt, assignedBulbId: null };
        }
        return pt;
      });
    });

    setBulbs((prev) =>
      prev.map((b) => {
        if (b.id === bulbId) {
          const isCurrentlyHere = (b.assignedPoints || []).includes(pointId);
          return {
            ...b,
            assignedPoints: isCurrentlyHere ? [] : [pointId],
          };
        }
        // Si se asignó pointId a este foco, quitarlo de cualquier otro foco
        const filtered = (b.assignedPoints || []).filter((p) => p !== pointId);
        return { ...b, assignedPoints: filtered };
      })
    );
  };

  // Asigna el foco a este punto o lo desasigna
  const assignAllPointsToBulb = (bulbId: string) => {
    // En la realidad física, 1 foco no puede estar en 8 sitios a la vez.
    // Mantenemos la función para compatibilidad colocándolo en 'top-left' si no tiene punto.
    assignBulbToPoint('top-left', bulbId);
  };

  const unassignAllPointsFromBulb = (bulbId: string) => {
    setPoints((prev) =>
      prev.map((pt) => (pt.assignedBulbId === bulbId ? { ...pt, assignedBulbId: null } : pt))
    );
    setBulbs((prev) =>
      prev.map((b) => (b.id === bulbId ? { ...b, assignedPoints: [] } : b))
    );
  };

  const updateBulbMaxBrightness = (bulbId: string, maxBrightness: number) => {
    setBulbs((prev) =>
      prev.map((b) => (b.id === bulbId ? { ...b, maxBrightness } : b))
    );
  };

  // Actualiza el brillo máximo en TODOS los focos a la vez (slider global)
  const updateGlobalMaxBrightness = (val: number) => {
    const clamped = Math.max(10, Math.min(100, Math.round(val)));
    setBulbs((prev) => prev.map((b) => ({ ...b, maxBrightness: clamped })));
  };

  const primaryBulb = bulbs.find((b) => b.isPrimary) || bulbs[0] || INITIAL_BULBS[0];
  const globalMaxBrightness = primaryBulb.maxBrightness ?? 100;

  return {
    points,
    bulbs,
    primaryBulb,
    algorithm,
    tune,
    globalBorderWidth,
    isCapturing,
    fps,
    animationMode,
    setAnimationMode,
    inspectorZone,
    setInspectorZone,
    masterColor,
    masterDimming,
    masterBrightness,
    masterWhite,
    setAlgorithm,
    updateTuneParam,
    updatePointBorderWidth,
    applyBorderToAll,
    assignBulbToPoint,
    toggleShareScreen,
    addBulb,
    removeBulb,
    setPrimaryBulb,
    togglePointAssignment,
    assignAllPointsToBulb,
    unassignAllPointsFromBulb,
    updateBulbMaxBrightness,
    globalMaxBrightness,
    updateGlobalMaxBrightness,
  };
}
